/**
 * @fileoverview
 * Selected Branch Service - Global selected branch for the application header.
 * Auto-selects the nearest accessible branch on login / initial session load
 * using browser geolocation; falls back to the first accessible branch.
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, inject, signal, computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { STORAGE_KEYS } from '@core/constants';
import { StorageUtil } from '@core/utils';
import {
  BranchService,
  type BranchDropdownOption,
} from './branch.service';
import { GeolocationService } from './geolocation.service';

/** Max branches fetched when resolving nearest (matches backend dropdown limit). */
const NEAREST_BRANCH_FETCH_LIMIT = 1000;

interface PersistedSelectedBranch {
  value: string;
  label: string;
  id: number;
  regionId?: number;
  regionUuid?: string;
  geoLat?: number;
  geoLng?: number;
}

@Injectable({
  providedIn: 'root',
})
export class SelectedBranchService {
  private readonly branchService = inject(BranchService);
  private readonly geolocationService = inject(GeolocationService);

  private readonly selectedBranchSignal = signal<BranchDropdownOption | null>(
    this.loadFromStorage()
  );
  private readonly accessibleBranchesSignal = signal<BranchDropdownOption[]>([]);
  private readonly initializingSignal = signal(false);

  /** Guards auto-select so it runs only once per login / session bootstrap. */
  private autoSelectStarted = false;

  readonly selectedBranch = this.selectedBranchSignal.asReadonly();
  readonly accessibleBranches = this.accessibleBranchesSignal.asReadonly();
  readonly initializing = this.initializingSignal.asReadonly();

  readonly selectedBranchLabel = computed(
    () => this.selectedBranchSignal()?.label ?? ''
  );
  readonly selectedBranchUuid = computed(
    () => this.selectedBranchSignal()?.value ?? null
  );
  readonly selectedBranchId = computed(
    () => this.selectedBranchSignal()?.id ?? null
  );
  readonly headerBranchLabels = computed(() =>
    this.accessibleBranchesSignal().map((b) => b.label)
  );

  /**
   * Run once after login or initial authenticated session load.
   * Requests location only when needed to pick the nearest accessible branch.
   * @param options.forceNearest - When true (fresh login), ignore persisted selection and resolve nearest.
   */
  initializeAfterAuth(options?: { forceNearest?: boolean }): void {
    if (this.autoSelectStarted) {
      return;
    }
    this.autoSelectStarted = true;
    void this.resolveInitialBranch(options?.forceNearest === true);
  }

  /**
   * Manual override from the header (or elsewhere). Persists for the session.
   */
  setSelectedBranch(branch: BranchDropdownOption | null): void {
    this.selectedBranchSignal.set(branch);
    if (branch) {
      this.saveToStorage(branch);
    } else {
      StorageUtil.removeLocal(STORAGE_KEYS.SELECTED_BRANCH);
    }
  }

  /**
   * Resolve selection by branch label (header dropdown uses labels).
   */
  setSelectedBranchByLabel(label: string | null | undefined): void {
    if (!label) {
      this.setSelectedBranch(null);
      return;
    }
    const match = this.accessibleBranchesSignal().find((b) => b.label === label);
    if (match) {
      this.setSelectedBranch(match);
    }
  }

  /**
   * Clear selection and allow auto-select on the next authenticated session.
   */
  clear(): void {
    this.autoSelectStarted = false;
    this.selectedBranchSignal.set(null);
    this.accessibleBranchesSignal.set([]);
    StorageUtil.removeLocal(STORAGE_KEYS.SELECTED_BRANCH);
  }

  private async resolveInitialBranch(forceNearest: boolean): Promise<void> {
    this.initializingSignal.set(true);
    try {
      if (forceNearest) {
        StorageUtil.removeLocal(STORAGE_KEYS.SELECTED_BRANCH);
        this.selectedBranchSignal.set(null);
      }

      const branches = await this.fetchAccessibleBranches();
      this.accessibleBranchesSignal.set(branches);

      if (branches.length === 0) {
        this.setSelectedBranch(null);
        return;
      }

      if (!forceNearest) {
        const persisted = this.selectedBranchSignal();
        if (persisted) {
          const stillAccessible = branches.find((b) => b.value === persisted.value);
          if (stillAccessible) {
            this.setSelectedBranch(stillAccessible);
            return;
          }
        }
      }

      const nearest = await this.findNearestBranch(branches);
      this.setSelectedBranch(nearest ?? this.fallbackBranch(branches));
    } catch {
      const branches = this.accessibleBranchesSignal();
      if (branches.length > 0 && !this.selectedBranchSignal()) {
        this.setSelectedBranch(this.fallbackBranch(branches));
      }
    } finally {
      this.initializingSignal.set(false);
    }
  }

  private async fetchAccessibleBranches(): Promise<BranchDropdownOption[]> {
    const response = await firstValueFrom(
      this.branchService.getBranchesForDropdown(
        undefined,
        NEAREST_BRANCH_FETCH_LIMIT
      )
    );
    return response.data ?? [];
  }

  private async findNearestBranch(
    branches: BranchDropdownOption[]
  ): Promise<BranchDropdownOption | null> {
    const withCoords = branches.filter(
      (b) =>
        b.geoLat != null &&
        b.geoLng != null &&
        this.geolocationService.isValidCoordinates(b.geoLat, b.geoLng)
    );

    if (withCoords.length === 0 || !this.geolocationService.isGeolocationSupported()) {
      return null;
    }

    try {
      const position = await this.geolocationService.getCurrentPosition();
      let nearest: BranchDropdownOption | null = null;
      let minDistance = Number.POSITIVE_INFINITY;

      for (const branch of withCoords) {
        const distance = this.geolocationService.calculateDistance(
          position.latitude,
          position.longitude,
          branch.geoLat!,
          branch.geoLng!
        );
        if (distance < minDistance) {
          minDistance = distance;
          nearest = branch;
        }
      }

      return nearest;
    } catch {
      // Permission denied or location unavailable — caller uses fallback.
      return null;
    }
  }

  /** Existing default: first accessible branch (API order is name ascending). */
  private fallbackBranch(
    branches: BranchDropdownOption[]
  ): BranchDropdownOption | null {
    return branches[0] ?? null;
  }

  private loadFromStorage(): BranchDropdownOption | null {
    const stored = StorageUtil.getLocal<PersistedSelectedBranch>(
      STORAGE_KEYS.SELECTED_BRANCH
    );
    if (!stored?.value || !stored?.label || stored.id == null) {
      return null;
    }
    return {
      value: stored.value,
      label: stored.label,
      id: stored.id,
      regionId: stored.regionId,
      regionUuid: stored.regionUuid,
      geoLat: stored.geoLat,
      geoLng: stored.geoLng,
    };
  }

  private saveToStorage(branch: BranchDropdownOption): void {
    const payload: PersistedSelectedBranch = {
      value: branch.value,
      label: branch.label,
      id: branch.id,
      regionId: branch.regionId,
      regionUuid: branch.regionUuid,
      geoLat: branch.geoLat,
      geoLng: branch.geoLng,
    };
    StorageUtil.setLocal(STORAGE_KEYS.SELECTED_BRANCH, payload);
  }
}
